import { useCallback, useMemo, useRef, useState } from "react";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import { ErrorCode, FileRejection, useDropzone } from "react-dropzone";
import { twJoin } from "tailwind-merge";

import { useUploadFile } from "src/api/filesApi/queries";
import { Button } from "src/base-components/Button";
import { FileCard } from "src/base-components/FileCard";
import { UploadFileEnvFilesPostEnvEnum } from "src/clients/files";
import { toastActions } from "src/design-system/Toast/utils";
import { Environment } from "src/hooks/useEnvironment";
import {
  AcceptedFileTypes,
  DEFAULT_ACCEPTED_FILE_TYPES,
  FileSource,
  getExtensionsFromAcceptedFileTypes,
  humanReadableSize,
  MAX_FILE_SIZE_BYTES,
} from "src/utils/files";

export type FileUploadFileItem = {
  key: string;
  fileId?: string;
  env?: Environment;
  file?: File;
  onDelete?: () => void;
};

type FileUploadProps = {
  /** Called when a file passes validation and is accepted by the dropzone. */
  onFileSelect: (file: File) => void;
  /** Allow selecting multiple files. When false, the upload button hides after one file. */
  multiple?: boolean;
  /** Files currently being uploaded — rendered as FileCards with a spinner. */
  uploadingFiles?: File[];
  /** Uploaded files to display as FileCards. */
  files?: FileUploadFileItem[];
  /** Disables the upload button and hides delete actions. */
  disabled?: boolean;
  /** Helper text shown above the file list (e.g. accepted formats, size limit). */
  description?: string;
  /** MIME-type-to-extensions map for the file picker. Defaults to all supported types. */
  accept?: AcceptedFileTypes;
  /** Maximum file size in bytes. Defaults to MAX_FILE_SIZE_BYTES (32 MB). */
  maxSize?: number;
  /**
   * Called when files are rejected by dropzone validation.
   * Defaults to showing an error toast per rejection.
   */
  onFileRejected?: (rejections: FileRejection[]) => void;
  dataLoc?: string;
};

const getDropzoneRejectionDescription = (
  fileName: string,
  errorCode: ErrorCode | string,
  accept: AcceptedFileTypes,
  maxSize: number,
): string => {
  const extensions = getExtensionsFromAcceptedFileTypes(accept);
  switch (errorCode) {
    case ErrorCode.FileInvalidType:
      return `Upload failed: '${fileName}' is not a supported file type. Supported types: ${extensions.join(", ")}.`;
    case ErrorCode.FileTooLarge:
      return `Upload failed: '${fileName}' exceeds the maximum file size limit of ${humanReadableSize(maxSize)}.`;
    default:
      return `Upload failed for '${fileName}'.`;
  }
};

const createFileExtensionValidator =
  (accept: AcceptedFileTypes) => (file: File) => {
    if ("name" in file) {
      const extension = file.name.split(".").pop()?.toLowerCase();
      if (!extension) {
        return {
          code: "FileExtensionMissing",
          message: "File must have an extension. E.g. .pdf, .jpg, or .png",
        };
      }
      const allowedExtensions = getExtensionsFromAcceptedFileTypes(accept);
      if (!allowedExtensions.includes(extension)) {
        return {
          code: ErrorCode.FileInvalidType,
          message: `Invalid file type. Supported types: ${allowedExtensions.join(", ")}`,
        };
      }
    }
    return null;
  };

const defaultOnUploadError = (file: File, retry: () => void) => {
  toastActions.failure({
    title: "File Upload Failed",
    description: `Upload failed for '${file.name}'. Please try again.`,
    actionText: "Try again",
    onActionClick: retry,
  });
};

const defaultOnFileRejected = (
  rejections: FileRejection[],
  accept: AcceptedFileTypes,
  maxSize: number,
) => {
  rejections.forEach((rejection) => {
    const description = getDropzoneRejectionDescription(
      rejection.file.name,
      rejection.errors[0].code,
      accept,
      maxSize,
    );
    toastActions.failure({ title: "File Upload Failed", description });
  });
};

const useFileUploadDropzone = ({
  onFileSelect,
  multiple,
  disabled,
  accept,
  maxSize,
  onFileRejected,
}: {
  onFileSelect: (file: File) => void;
  multiple: boolean;
  disabled: boolean;
  accept: AcceptedFileTypes;
  maxSize: number;
  onFileRejected?: (rejections: FileRejection[]) => void;
}) => {
  const handleDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      for (const file of acceptedFiles) {
        onFileSelect(file);
      }
      if (rejectedFiles.length > 0) {
        if (onFileRejected) {
          onFileRejected(rejectedFiles);
        } else {
          defaultOnFileRejected(rejectedFiles, accept, maxSize);
        }
      }
    },
    [onFileSelect, accept, maxSize, onFileRejected],
  );

  return useDropzone({
    onDrop: handleDrop,
    useFsAccessApi: true,
    maxFiles: multiple ? undefined : 1,
    multiple,
    maxSize,
    accept,
    validator: createFileExtensionValidator(accept),
    disabled,
    noClick: true,
    noKeyboard: true,
  });
};

export const FileUpload: React.FC<FileUploadProps> = ({
  onFileSelect,
  multiple = false,
  uploadingFiles,
  files,
  disabled = false,
  description,
  accept = DEFAULT_ACCEPTED_FILE_TYPES,
  maxSize = MAX_FILE_SIZE_BYTES,
  onFileRejected,
  dataLoc,
}) => {
  const hasFiles = files && files.length > 0;
  const hasUploadingFiles = uploadingFiles && uploadingFiles.length > 0;
  const showUploadButton = multiple || (!hasFiles && !hasUploadingFiles);

  const { getRootProps, getInputProps, isDragActive, open } =
    useFileUploadDropzone({
      onFileSelect,
      multiple,
      disabled: disabled || !showUploadButton,
      accept,
      maxSize,
      onFileRejected,
    });

  return (
    <div
      {...getRootProps({
        "data-loc": dataLoc,
        className: twJoin(
          "-m-2 flex flex-col gap-2 rounded-lg border border-transparent border-dashed p-2 transition-colors",
          isDragActive && "border-indigo-400 bg-indigo-50",
        ),
      })}
    >
      <input
        {...getInputProps()}
        data-loc={dataLoc ? `${dataLoc}-input` : undefined}
      />
      {description && (
        <span className="font-inter-normal-12px text-gray-500">
          {description}
        </span>
      )}
      {files?.map((item) => {
        const itemDataLoc = dataLoc ? `${dataLoc}-file-${item.key}` : undefined;
        if (item.fileId && item.env) {
          return (
            <FileCard
              key={item.key}
              dataLoc={itemDataLoc}
              disabled={disabled}
              file={item.file}
              fileEnv={item.env}
              fileId={item.fileId}
              onDelete={item.onDelete}
            />
          );
        }
        if (item.file) {
          return (
            <FileCard
              key={item.key}
              dataLoc={itemDataLoc}
              disabled={disabled}
              file={item.file}
              onDelete={item.onDelete}
            />
          );
        }
        return null;
      })}
      {uploadingFiles?.map((file, index) => (
        <FileCard
          key={`uploading-${file.name}-${index}`}
          dataLoc={dataLoc ? `${dataLoc}-uploading-${index}` : undefined}
          file={file}
          isUploading
        />
      ))}
      {showUploadButton && (
        <div className="w-fit">
          <Button
            dataLoc={dataLoc ? `${dataLoc}-upload-btn` : undefined}
            disabled={disabled}
            iconLeft={faPlus}
            size="sm"
            variant="secondary"
            onClick={open}
          >
            {multiple ? "Upload files" : "Upload file"}
          </Button>
        </div>
      )}
    </div>
  );
};

type UseFileUploadOptions = {
  multiple?: boolean;
  env: Environment;
  source: FileSource;
  value?: string | string[];
  onChange?: (value: string | string[] | undefined) => void;
  disabled?: boolean;
  /** Called when a file upload fails. Defaults to showing an error toast with retry. */
  onUploadError?: (file: File, retry: () => void) => void;
};

type UseFileUploadReturn = {
  fileUploadProps: FileUploadProps;
};

/**
 * Headless hook that manages file upload state and API integration.
 * Returns props to spread onto <FileUpload> — handles upload, tracking,
 * optimistic UI (uploading spinners), and cleanup on failure.
 */
export const useFileUpload = ({
  multiple = false,
  env,
  source,
  value,
  onChange,
  disabled = false,
  onUploadError,
}: UseFileUploadOptions): UseFileUploadReturn => {
  const { onFileSelect, onFileUpload, onFileRemove, getFile } = useUploadFile();
  const [uploadingFiles, setUploadingFiles] = useState<File[]>([]);

  const fileIds: string[] = useMemo(() => {
    if (Array.isArray(value)) return value;
    if (value) return [value];
    return [];
  }, [value]);

  const fileIdsRef = useRef(fileIds);
  fileIdsRef.current = fileIds;

  const handleFileSelect = useCallback(
    async (file: File) => {
      const uploadKey = `file-upload-${source}-${Date.now()}-${file.name}`;
      onFileSelect(file, uploadKey);
      setUploadingFiles((prev) => [...prev, file]);

      try {
        const id = await onFileUpload(
          file,
          uploadKey,
          env as UploadFileEnvFilesPostEnvEnum,
          source,
        );
        if (id) {
          // Re-register under ID-based key so getFile can find it later
          onFileSelect(file, `file-upload-${source}-${id}`);
          if (multiple) {
            const newIds = [...fileIdsRef.current, id];
            onChange?.(newIds);
          } else {
            onChange?.(id);
          }
        }
      } catch {
        const retry = () => handleFileSelect(file);
        if (onUploadError) {
          onUploadError(file, retry);
        } else {
          defaultOnUploadError(file, retry);
        }
        onFileRemove(uploadKey);
      } finally {
        setUploadingFiles((prev) => prev.filter((f) => f !== file));
      }
    },
    [
      onFileSelect,
      onFileUpload,
      onFileRemove,
      env,
      source,
      multiple,
      onChange,
      onUploadError,
    ],
  );

  const handleRemove = useCallback(
    (idToRemove: string) => {
      if (multiple) {
        const newIds = fileIdsRef.current.filter((id) => id !== idToRemove);
        onChange?.(newIds.length > 0 ? newIds : undefined);
      } else {
        onChange?.(undefined);
      }
    },
    [multiple, onChange],
  );

  const files: FileUploadFileItem[] = fileIds.map((id) => ({
    key: id,
    fileId: id,
    env,
    file: getFile(`file-upload-${source}-${id}`) ?? undefined,
    onDelete: () => handleRemove(id),
  }));

  return {
    fileUploadProps: {
      onFileSelect: handleFileSelect,
      multiple,
      uploadingFiles,
      disabled,
      files,
    },
  };
};
